import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { AppBar, Dialog, IconButton, Toolbar, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { EngineerSignoffComponent } from "components/job/completion/EngineerSignoffComponent";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import { isAbortError, isEmpty } from "helpers";
import engineerSettings from "helpers/engineerSettings";
import { JobStatus } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  completeVisit,
  downloadJobs,
  filterNewJobIds,
  getJobs,
  selectIncompleteJobs,
  selectSelectedJob,
  selectSelectedJobVisit,
  setJobStatus,
  setSignatureCustomer,
  setSignatureEngineer,
  setVisitValue,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { CustomerSignoffComponent } from "./CustomerSignoffComponent";

interface SignoffDialogProps {
  open: boolean;
  handleClose: () => void;
}

export const SignoffDialog: FC<SignoffDialogProps> = (props: SignoffDialogProps) => {
  const { open, handleClose } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const jobs = useAppSelector(selectIncompleteJobs, shallowEqual);
  const jobVisit = useAppSelector(selectSelectedJobVisit);
  const { skipSignature, customerSignerName, signoffErrors, submitLoading } = jobVisit;

  const [openCustomerReview, setOpenCustomerReview] = useState(false);
  const [isCustomerSignatureAdded, setCustomerSignatureAdded] = useState(false);
  const [isEngineerSignatureAdded, setEngineerSignatureAdded] = useState(false);

  const requireEngineerSignature = engineerSettings.requireEngineerSignature ?? false;
  const requireCustomerSignature = engineerSettings.requireCustomerSignature ?? false;
  const requireCustomerSignatureName = engineerSettings.requireCustomerSignatureName ?? false;

  const isValid = useCallback(() => {
    return !Object.keys(signoffErrors).length;
  }, [signoffErrors]);

  const onClose = useCallback(() => {
    handleClose();
    setCustomerSignatureAdded(false);
    setEngineerSignatureAdded(false);
    setOpenCustomerReview(false);
  }, [handleClose]);

  const onSetVisitValue = useCallback(
    (key: "followUp" | "solutionDescription", value: any) => {
      dispatch(setVisitValue({ key, value }));
    },
    [dispatch]
  );

  const handleComplete = useCallback(() => {
    dispatch(completeVisit({ jobId: job.id }))
      .then(unwrapResult)
      .then(({ queued, followUp }) => {
        dispatch(setJobStatus({ jobId: job.id, status: JobStatus.Completed }));
        dispatch(getJobs({ force: true })).then(() => {
          if (jobs.length > 0) {
            var jobIds: string[] = [];
            jobs.forEach((job) => {
              if (job.status === JobStatus.Assigned) {
                jobIds.push(job.id);
              }
            });
            if (jobIds.length > 0) {
              dispatch(
                downloadJobs({
                  jobIds,
                })
              );
            }
          }
        });
        dispatch(filterNewJobIds({ jobId: job.id }));
        if (queued) {
          dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-stored" }));
        } else {
          if (followUp) {
            dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-returned" }));
          } else {
            dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-success" }));
          }
        }
        onClose();
        navigate("/jobs");
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-fail" }));
      });
  }, [dispatch, onClose, job.id, jobs, navigate]);

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            {openCustomerReview ? (
              <FormattedMessage id="general.customer" />
            ) : (
              <FormattedMessage id="general.engineer" />
            )}{" "}
            / {`${job?.externalId} `} <FormattedMessage id="visit.signoff.summary" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        {!openCustomerReview ? (
          <EngineerSignoffComponent
            job={job}
            jobVisit={jobVisit}
            onSetVisitValue={onSetVisitValue}
            handleSignature={(value: string | undefined) => {
              const hasValue = !isEmpty(value);
              if (hasValue) {
                dispatch(
                  setSignatureEngineer({
                    signatureData: value,
                  })
                );
              } else {
                dispatch(
                  setSignatureEngineer({
                    signatureData: undefined,
                  })
                );
              }
              setEngineerSignatureAdded(hasValue);
            }}
            disableNextButton={
              submitLoading || (requireEngineerSignature && !isEngineerSignatureAdded)
            }
            loading={submitLoading}
            openCustomerReview={() => {
              setOpenCustomerReview(true);
            }}
          />
        ) : (
          <CustomerSignoffComponent
            jobVisit={jobVisit}
            handleComplete={handleComplete}
            handleSignature={(value) => {
              const hasValue = !isEmpty(value);
              if (hasValue) {
                dispatch(
                  setSignatureCustomer({
                    signatureData: value,
                  })
                );
              } else {
                dispatch(
                  setSignatureCustomer({
                    signatureData: undefined,
                  })
                );
              }
              setCustomerSignatureAdded(hasValue);
            }}
            disableCompleteVisitButton={
              submitLoading ||
              (requireCustomerSignatureName && !customerSignerName) ||
              (requireCustomerSignature && !isCustomerSignatureAdded) ||
              (!skipSignature && (!isValid() || !isCustomerSignatureAdded)) ||
              !customerSignerName
            }
            loading={submitLoading}
          />
        )}
      </StyledContainer>
    </Dialog>
  );
};
